import React from "react";
import { createShortLink } from "actions/shortLink";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik } from "formik";
import Padder from "components/Padder";
import ProductsFields from "components/ShortLinkForm/Fields/Products";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import TrialFields from "components/ShortLinkForm/Fields/Trial";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import ConfirmationFields from "components/ShortLinkForm/Fields/Confirmation";
import ReceiverFields from "components/ShortLinkForm/Fields/Receiver";
import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import {
  prepareCreateShortLinkInitialValues,
  prepareCreateShortLink,
  getShortLinkPreviewTitle,
  validateCreateShortLinkForm
} from "utils/shortLink";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import FormActions from "components/FormActions";
import {
  ensureAppCheckout,
  ensureAttachments,
  ensureProductsPricesCouponsPromotionCodes,
  ensureShippingRates,
  ensureTaxRates
} from "utils/hooks";
import { useCtxReferences } from "utils/selectors";
import get from "lodash/get";
import compact from "lodash/compact";
import InfoList from "components/InfoList";
import { MAIN_CONTENT_MAX_WIDTH, THEMES } from "utils/constants/ui";
import { usePrevPathFragment } from "utils/hooks/hub";
import { useRouter } from "next/router";
import Link from "next/link";
import HoverIconButton from "components/HoverIconButton";
import CheckoutDetails from "components/SaleForm/Fields/CheckoutDetails";
import { STATE_KEYS } from "utils/constants/state";
import { fetchAttachments } from "actions/attachment";
import { fetchAppCheckout } from "actions/app";
import { PAYMENT_MODE } from "utils/constants/payment";
import { SHORT_LINK_NODE_KEY } from "utils/constants/shortLink";

const ShortLinkCreate = ({ actions, afterCreate }) => {
  const backPath = usePrevPathFragment();

  const {
    products,
    prices,
    coupons,
    promotionCodes
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });

  const { taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates: actions.fetchMerchantAccountTaxRates
  });
  const { shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates: actions.fetchMerchantAccountShippingRates
  });
  const { attachments } = ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });

  ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });

  const { replace } = useRouter();

  const {
    application,
    merchantAccount,
    resourceID,
    resourceUUID,
    resource
  } = useCtxReferences();

  const goShow = (uuid) => {
    replace(`${backPath}/${uuid}`);
  };

  const submitForm = (formValues, formikActions) => {
    const data = prepareCreateShortLink({
      values: formValues,
      products,
      prices,
      merchantAccount,
      coupons,
      promotionCodes,
      attachments
    });

    actions.createShortLink(
      {
        resourceID,
        resourceUUID,
        resource,
        data
      },
      (err, responseData) => {
        /**
         * 1. Turn off loader
         */
        formikActions.setSubmitting(false);
        /**
         * 2. Show errors or reset form
         */
        if (err) {
          formikActions.setErrors(err);
        } else {
          formikActions.resetForm({ values: formValues });
        }

        /**
         * 3a. Call afterCreate handler or
         * 3b. Perform default conditional navigation
         */
        if (afterCreate) {
          afterCreate(err, responseData);
        } else {
          responseData.shortLink && responseData.shortLink.uuid
            ? goShow(responseData.shortLink.uuid)
            : replace(backPath);
        }
      }
    );
  };

  const initialValues = prepareCreateShortLinkInitialValues({
    application,
    merchantAccount
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateCreateShortLinkForm({
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates
      })}
    >
      {({ values, errors }) => {
        const primaries = get(values, STATE_KEYS.SHORT_LINK.PRIMARIES);
        const mode = get(values, STATE_KEYS.SHORT_LINK.MODE);
        const checkoutPreview = shortLinkCheckoutPreview({
          shortLink: values,
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        });

        const showPreview =
          (primaries[0] && primaries[0].product && primaries[0].price) ||
          mode === PAYMENT_MODE.SETUP;
        const previewSubtotal = showPreview
          ? getShortLinkPreviewTitle({
              shortLink: values,
              products,
              prices,
              coupons,
              taxRates,
              shippingRates
            })
          : "";

        const messagesList = errors &&
          errors.messages &&
          errors.messages.length > 0 && (
            <div className="pt3 w-100 dib">
              <InfoList
                header="Error"
                items={errors.messages}
                theme={THEMES.ERROR}
              />
            </div>
          );

        return (
          <div className="w-100">
            <div className={MAIN_CONTENT_MAX_WIDTH}>
              <div className="sticky top-0 pv3 bg-white z-1 bb hairline-1 z-5 ph3">
                {previewSubtotal && (
                  <div className="f3 fw6 pb3">{previewSubtotal}</div>
                )}
                <FormActions
                  theme="slim"
                  customClasses={{
                    container:
                      "w-100 flex flex-row justify-between items-center"
                  }}
                >
                  <div className="flex justify-between items-center">
                    <Link href={backPath}>
                      <div className="flex flex-row items-center pointer">
                        <HoverIconButton type="back" tooltip="All links" />
                        <div className="pl1 f6 fw5 pointer">Back</div>
                      </div>
                    </Link>
                  </div>
                </FormActions>
              </div>
              <div className="ph3">
                {compact([
                  messagesList
                    ? {
                        key: SHORT_LINK_NODE_KEY.MESSAGES,
                        node: messagesList
                      }
                    : null,
                  {
                    key: SHORT_LINK_NODE_KEY.PRODUCTS,
                    node: <ProductsFields />
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.PAYMENT,
                    node: (
                      <PaymentFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                    )
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.TRIAL,
                    node: <TrialFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.TAX,
                    node: (
                      <TaxRatesFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                    )
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.COUPONS,
                    node: (
                      <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                    )
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.RECEIVER,
                    node: (
                      <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                    )
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.SHIPPING,
                    node: (
                      <ShippingFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                    )
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.REDIRECT,
                    node: <RedirectsFields />
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.CONFIRMATION,
                    node: (
                      <ConfirmationFields
                        name={STATE_KEYS.SHORT_LINK.CONFIRMATION_DATA}
                      />
                    )
                  },
                  {
                    key: SHORT_LINK_NODE_KEY.CHECKOUT_PREVIEW,
                    node: (
                      <CheckoutDetails
                        header="Checkout preview"
                        collapseImages={checkoutPreview.collapseImages}
                        {...checkoutPreview.checkout}
                      />
                    )
                  }
                ]).map(({ node, key }, nodeIx) => (
                  <div className={nodeIx ? "pt3 pt4-ns" : ""} key={key}>
                    {node}
                  </div>
                ))}
              </div>
            </div>
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createShortLink,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments,
      fetchAppCheckout
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ShortLinkCreate);
